/**
 * Service-worker registration. Runs idempotently — safe to call from any
 * entry point. The SW itself lives at /sw.js and is generated by Vite from
 * `viz/public/sw.js` (copied verbatim).
 *
 * No-ops when:
 *   - the runtime doesn't support service workers (older Safari, etc.)
 *   - the page is loaded from `file://` or another non-HTTP origin
 */
export function registerPwa(): void {
  if (typeof navigator === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  if (!/^https?:$/.test(window.location.protocol)) return;

  // Defer until after the page has finished loading so the SW install never
  // competes with the initial render.
  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .catch((err) => {
        // Surface only in dev — production users don't need to see this.
        // eslint-disable-next-line no-console
        console.warn("[pwa] service worker registration failed:", err);
      });
  });
}

export async function disablePwaForRemoteRuntime(): Promise<void> {
  if (typeof navigator === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  if (!/^https?:$/.test(window.location.protocol)) return;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));

    if ("caches" in globalThis) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames
          .filter((cacheName) => cacheName.startsWith("pulsefx-"))
          .map((cacheName) => caches.delete(cacheName)),
      );
    }
  } catch (err) {
    // A blocked storage API must not prevent the remote renderer from booting.
    // eslint-disable-next-line no-console
    console.warn("[pwa] remote cache cleanup failed:", err);
  }
}
